import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, ClockCounterClockwise, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { fetchMyActivity, fetchProjectActivity } from "@/lib/api";
import { formatDate } from "@/lib/dates";

// Notifications bell. Pulls recent activity (global or project-scoped) and
// renders a click-to-open dropdown. The bell shows an unread dot when there
// are entries newer than the per-user "lastSeen" timestamp (localStorage).

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return formatDate(iso.slice(0, 10));
}

const ACTION_TONE = {
  "task.created": "text-emerald-600",
  "task.updated": "text-indigo-600",
  "task.deleted": "text-rose-600",
  "task.reordered": "text-slate-600",
  "project.updated": "text-amber-600",
};

export default function NotificationsBell({ projectId, email }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const seenKey = projectId ? `tg_seen_${projectId}` : "tg_seen_global";
  const [lastSeen, setLastSeen] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(seenKey) || null;
  });
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  // Anchor coords for the fixed-position popup. Computed from the bell's rect
  // so the popup stays positioned correctly even when the header is inside an
  // `overflow-hidden` container (which would otherwise clip an absolutely
  // positioned child).
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  // Recompute popup anchor when opened or window resizes.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (!r) return;
      const margin = 12;
      const right = Math.max(margin, window.innerWidth - r.right);
      const top = Math.min(window.innerHeight - margin, r.bottom + 8);
      setCoords({ top, right });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Close on outside click — works for the portal-rendered panel too because
  // we check the panel's own ref instead of the wrapper.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Fetch on open
  const load = async () => {
    setLoading(true);
    try {
      const data = projectId
        ? await fetchProjectActivity(projectId, email, 50)
        : await fetchMyActivity(30);
      setItems(data);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Lightweight polling so the unread dot stays fresh without WebSockets.
  useEffect(() => {
    let cancel = false;
    const tick = async () => {
      try {
        const data = projectId
          ? await fetchProjectActivity(projectId, email, 10)
          : await fetchMyActivity(10);
        if (!cancel) setItems((prev) => (prev.length ? prev : data));
        if (!cancel && data[0] && (!lastSeen || new Date(data[0].created_at) > new Date(lastSeen))) {
          // touch state to flip the unread dot on
          setItems(data);
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancel = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, email]);

  const unread = items[0] && (!lastSeen || new Date(items[0].created_at) > new Date(lastSeen));

  const markSeen = () => {
    if (items[0]) {
      const ts = items[0].created_at;
      localStorage.setItem(seenKey, ts);
      setLastSeen(ts);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) {
            load();
            markSeen();
          }
        }}
        className="h-9 w-9 rounded-md border border-slate-300 bg-white hover:border-slate-950 hover:text-slate-950 text-slate-600 flex items-center justify-center transition-colors relative"
        title="Recent activity"
        data-testid="notifications-bell"
      >
        <Bell size={16} weight="bold" />
        {unread && (
          <span
            className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white"
            data-testid="notifications-unread-dot"
          />
        )}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          className="fixed w-[360px] max-w-[calc(100vw-24px)] bg-white border border-slate-200 rounded-lg shadow-xl z-[100]"
          style={{ top: coords.top, right: coords.right }}
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={16} weight="bold" className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-950">
                {projectId ? "This project" : "Recent activity"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 flex items-center justify-center"
              aria-label="Close"
            >
              <X size={12} weight="bold" />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-xs text-slate-400">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400">No activity yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((it) => (
                  <li
                    key={it.id}
                    className="px-4 py-3 hover:bg-slate-50 transition-colors"
                    data-testid={`notification-item-${it.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${ACTION_TONE[it.action] || "text-slate-400"}`} style={{ background: "currentColor" }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-900 leading-snug">
                          <span className="font-semibold">{it.actor_name}</span>{" "}
                          <span className="text-slate-700">{it.summary}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                          <span>{relTime(it.created_at)}</span>
                          {it.project_name && !projectId && (
                            <>
                              <span>·</span>
                              <Link to={`/project/${it.project_id}`} className="hover:text-slate-700 truncate">
                                {it.project_name}
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
